import { createApi, fetchBaseQuery, type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { API_BASE_URL, STORAGE_KEYS } from '@utils/constants'
import type { RootState } from './store'
import { logout, setCredentials } from '@features/auth/authSlice'
import type { User } from '@features/auth/authTypes'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const stateToken = (getState() as RootState).auth.token
    const token = stateToken ?? localStorage.getItem(STORAGE_KEYS.token)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return headers
  },
})

const refreshMutex = new Mutex()

interface RefreshResponse {
  response?: {
    data?: {
      user?: User
      access_token?: string
    }
  }
  access_token?: string
  user?: User
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await refreshMutex.waitForUnlock()
  let result = await rawBaseQuery(args, api, extraOptions)

  if (result.error?.status === 401) {
    if (!refreshMutex.isLocked()) {
      const release = await refreshMutex.acquire()
      try {
        const refreshResult = await rawBaseQuery(
          { url: '/auth/refresh', method: 'POST' },
          api,
          extraOptions,
        )
        const raw = refreshResult.data as RefreshResponse | undefined
        const data = raw?.response?.data ?? raw
        const newToken = data?.access_token
        const newUser = data?.user

        if (newToken) {
          api.dispatch(
            setCredentials({
              token: newToken,
              user: newUser ?? (api.getState() as RootState).auth.user!,
            }),
          )
          result = await rawBaseQuery(args, api, extraOptions)
        } else {
          api.dispatch(logout())
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.assign('/login')
          }
        }
      } finally {
        release()
      }
    } else {
      await refreshMutex.waitForUnlock()
      result = await rawBaseQuery(args, api, extraOptions)
    }
  }

  return result
}

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Auth', 'Booking', 'Feedback', 'Notification', 'Monetization', 'AiUsage', 'LookupCategory', 'LookupValue'],
  endpoints: () => ({}),
})
